import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { fetchActiveMenu, getLoyaltyStatus, type ActiveMenuDto } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { useCartStore } from '../stores/cart.store.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { GuestActions } from '../components/GuestActions.js'
import { LoyaltyCard } from '../components/LoyaltyCard.js'
import { ShareCodeCard } from '../components/ShareCodeCard.js'
import { ActiveOrderCard } from '../components/ActiveOrderCard.js'
import { ScrollTopButton } from '../components/ScrollTopButton.js'
import { useStadiumMode, useSeatStore, formatSeat } from '../lib/stadium.js'

export function Menu() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const setLoyaltyProgram = useSessionStore((s) => s.setLoyaltyProgram)
  const lines = useCartStore((s) => s.lines)
  const subtotalCents = useCartStore((s) => s.subtotalCents())

  const [menu, setMenu] = useState<ActiveMenuDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  // Modo estadio (por negocio). Sin él, este menú es idéntico al de siempre.
  const stadium = useStadiumMode()
  const seat = useSeatStore((s) => (slug ? s.bySlug[slug] ?? null : null))
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    if (!session) {
      navigate(`/r/${slug}`, { replace: true })
      return
    }
    void (async () => {
      const r = await fetchActiveMenu(session.tenantId, session.token, session.branchId)
      if (!r.ok) setError(`${r.error.code}: ${r.error.message}`)
      else {
        setMenu(r.data.menu)
        if (r.data.menu.sections.length > 0) setActiveSection(r.data.menu.sections[0]!.id)
      }
    })()
  }, [session, slug, navigate])

  // Modo estadio: la píldora de categoría sigue al scroll. Sin el modo no cambia nada.
  useEffect(() => {
    if (!stadium || !menu) return
    const onScroll = (): void => {
      let current: string | null = null
      for (const sec of menu.sections) {
        const el = sectionRefs.current[sec.id]
        if (el && el.getBoundingClientRect().top <= 170) current = sec.id
      }
      // Al llegar al fondo, la última categoría (puede ser corta y no alcanzar el umbral).
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
      if (atBottom && menu.sections.length > 0) current = menu.sections[menu.sections.length - 1]!.id
      if (current) setActiveSection(current)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [stadium, menu])

  // Modo estadio: la píldora activa se desliza a la vista en la fila de categorías.
  useEffect(() => {
    if (!stadium || !activeSection) return
    const tab = document.querySelector<HTMLElement>(`[data-section-tab="${activeSection}"]`)
    tab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [stadium, activeSection])

  // Estado del programa de lealtad del negocio (gatea el hook de "acumula puntos"
  // en LoyaltyCard). Best-effort: si falla, LoyaltyCard asume OFF y muestra el
  // nudge neutro de pre-registro (igual alimenta el CRM).
  useEffect(() => {
    if (!session) return
    void (async () => {
      const r = await getLoyaltyStatus(session.tenantId)
      setLoyaltyProgram(r.ok ? r.data : { enabled: false, pointsPerVisit: 0 })
    })()
  }, [session, setLoyaltyProgram])

  const linesCount = useMemo(() => lines.reduce((a, l) => a + l.quantity, 0), [lines])

  // Pedido takeout en curso enviado desde este dispositivo: si el comensal
  // regresó al menú (flecha atrás desde el tracker), que su pedido siga A LA
  // VISTA — antes quedaba un menú vacío y parecía que el pedido se borró.
  const allActiveOrders = useActiveOrderStore((s) => s.orders)
  // Pedidos takeout en curso de ESTA sesión, más nuevo primero.
  const sessionOrders = allActiveOrders
    .filter((o) => o.tableSessionId === session?.tableSessionId)
    .slice()
    .sort((a, b) => b.placedAt - a.placedAt)

  if (!session) return <></>

  function scrollToSection(id: string): void {
    setActiveSection(id)
    const el = sectionRefs.current[id]
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <ScrollTopButton />
      <div className="container">
        {/* Va ARRIBA de todo: si el comensal ya pidió, lo primero que debe ver
            al volver al menú es cómo regresar a su pedido. */}
        {/* La lista de pedidos en curso (con códigos) manda cuando existe. */}
        {stadium && (
          <section className="st-hero st-hero--menu">
            <span className="st-hero__eyebrow">Pide desde tu asiento</span>
            <h1 className="st-hero__title">¿Qué se te antoja?</h1>
            <Link to={`/r/${slug}/cart`} className="st-seat-chip" aria-label="Tu ubicación">
              <span aria-hidden>📍</span>
              {seat ? formatSeat(seat) : 'Agrega tu ubicación al pagar'}
            </Link>
          </section>
        )}
        {!stadium && sessionOrders.length === 0 && <ActiveOrderCard slug={slug} />}
        {/* Pedido(s) en curso: prominente, arriba de todo. El comensal que dio
            "atrás" NO pierde de vista sus pedidos. Para llevar cada uno es
            independiente (su propio código), así que si hizo varios, se listan
            TODOS con su código para que los muestre juntos al recoger. */}
        {sessionOrders.length > 0 && (
          <div
            className="card"
            style={{ border: '1px solid var(--accent)', background: 'var(--accent-soft)' }}
          >
            <strong>
              {sessionOrders.length === 1 ? 'Tu pedido está en curso' : `Tienes ${sessionOrders.length} pedidos en curso`}
            </strong>
            <div className="muted" style={{ marginTop: 2, marginBottom: 10 }}>
              Toca para ver el seguimiento y {sessionOrders.length === 1 ? 'tu código' : 'los códigos'} de recolección.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessionOrders.map((o, i) => (
                <Link
                  key={o.orderId}
                  to={`/r/${slug}/order/${o.orderId}`}
                  className="row"
                  style={{
                    textDecoration: 'none', color: 'inherit', alignItems: 'center',
                    background: '#fff', border: '1px solid var(--line)', borderRadius: 12, padding: '10px 12px',
                  }}
                >
                  <div className="grow">
                    <strong>Pedido {sessionOrders.length - i}</strong>
                    {o.pickupCode ? (
                      <span className="muted" style={{ marginLeft: 8 }}>{o.pickupCode}</span>
                    ) : null}
                  </div>
                  <span aria-hidden="true" style={{ fontSize: 18 }}>→</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {!stadium && <ShareCodeCard />}
        {!stadium && <LoyaltyCard />}
        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <strong className="danger">No pudimos cargar el menú</strong>
            <div className="muted">{error}</div>
          </div>
        )}
        {!menu && !error && (
          <div className="card" style={{ textAlign: 'center' }}>
            <span className="spinner" />
            <div className="muted" style={{ marginTop: 8 }}>Cargando menú…</div>
          </div>
        )}
        {menu && menu.sections.length === 0 && (
          <div className="card">
            <strong>Menú vacío</strong>
            <div className="muted">Aún no hay productos publicados.</div>
          </div>
        )}
        {menu && menu.sections.length > 0 && (
          <>
            <div className="section-tabs" role="tablist">
              {menu.sections.map((sec) => (
                <button
                  key={sec.id}
                  role="tab"
                  data-section-tab={sec.id}
                  className={`section-tab ${activeSection === sec.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(sec.id)}
                >
                  {sec.name}
                </button>
              ))}
            </div>
            {menu.sections.map((sec) => (
              <div
                key={sec.id}
                className="menu-section"
                ref={(el) => {
                  sectionRefs.current[sec.id] = el
                }}
              >
                <h2>{sec.name}</h2>
                <div className="menu-grid">
                  {sec.items.map((it) => (
                    <Link
                      key={it.id}
                      to={`/r/${slug}/item/${it.id}`}
                      state={{ item: it }}
                      className="menu-item"
                    >
                      <div className="thumb">
                        {it.imageUrl ? (
                          <img src={it.imageUrl} alt={it.name} loading="lazy" />
                        ) : (
                          <span>{it.name.slice(0, 1)}</span>
                        )}
                      </div>
                      <div className="body">
                        <div className="name">{it.name}</div>
                        {it.description && <div className="desc">{it.description}</div>}
                        <div className="price">{formatMoney(it.priceCents, it.currency)}</div>
                      </div>
                      {stadium && <span className="st-add" aria-hidden>+</span>}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {stadium && linesCount > 0 && (
        <div className="st-cartbar">
          <Link to={`/r/${slug}/cart`} className="st-btn st-btn--primary st-cartbar__btn">
            <span>Ver carrito</span>
            <span className="st-cartbar__meta">
              {formatMoney(subtotalCents, 'MXN')}
              <span className="st-cartbar__count">{linesCount}</span>
            </span>
          </Link>
        </div>
      )}

      {!stadium && linesCount > 0 && (
        <div className="cart-bar">
          <div className="grow">
            <div style={{ fontWeight: 600 }}>
              {linesCount} producto{linesCount > 1 ? 's' : ''}
            </div>
            <div className="muted">{formatMoney(subtotalCents, 'MXN')}</div>
          </div>
          <Link to={`/r/${slug}/cart`} className="btn btn-primary" style={{ width: 'auto' }}>
            Revisar pedido
          </Link>
        </div>
      )}

      {!stadium && <GuestActions aboveCartBar={linesCount > 0} showBill={session.orderMode !== 'TAKEOUT'} />}

      <div className="footer">
        Powered by Nintai<em>GO</em>
      </div>
    </div>
  )
}
